import { createStore, del as idbDel, get as idbGet, keys as idbKeys, set as idbSet } from 'idb-keyval';
import type { CartDetailsProps } from '@/hooks/CartContext';
import {
  getLegacyCartDetailsFromLocalStorage,
  hasLegacyCartDetailsInLocalStorage,
  removeLegacyCartDetailsFromLocalStorage,
} from './storageFunctions';

export type CartDetailsMap = Record<number, CartDetailsProps>;

const STORE_DB_NAME = 'PWA_RCA_CARTS';
const STORE_OBJECT_NAME = 'cart_details';
const CART_KEY_PREFIX = 'cart:';
const LEGACY_BLOB_KEY = 'cart_details';

const idbStore = createStore(STORE_DB_NAME, STORE_OBJECT_NAME);

export interface CartDetailsBackend {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  keys(): Promise<string[]>;
}

const createIdbBackend = (): CartDetailsBackend => ({
  getItem: async (key) => {
    const v = await idbGet<string>(key, idbStore);
    return v ?? null;
  },
  setItem: async (key, value) => {
    await idbSet(key, value, idbStore);
  },
  removeItem: async (key) => {
    await idbDel(key, idbStore);
  },
  keys: async () => {
    const all = await idbKeys(idbStore);
    return all.filter((k): k is string => typeof k === 'string');
  },
});

let backend: CartDetailsBackend | null = null;

export const __setCartBackendForTests = (testBackend: CartDetailsBackend | null) => {
  backend = testBackend;
};

const getBackend = (): CartDetailsBackend => {
  if (!backend) backend = createIdbBackend();
  return backend;
};

const cartKey = (clientId: number) => `${CART_KEY_PREFIX}${clientId}`;

const isClientCartKey = (key: string) => key.startsWith(CART_KEY_PREFIX);

const parseClientIdFromKey = (key: string): number | null => {
  if (!isClientCartKey(key)) return null;
  const id = Number(key.slice(CART_KEY_PREFIX.length));
  return Number.isFinite(id) ? id : null;
};

const parseClientCart = (raw: string | null): CartDetailsProps | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CartDetailsProps;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const parseLegacyCartBlob = (raw: string | null): CartDetailsMap | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CartDetailsMap;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const hasCartEntries = (map: CartDetailsMap | null | undefined): boolean =>
  !!map && typeof map === 'object' && Object.keys(map).length > 0;

const isEmptyClientCart = (cart: CartDetailsProps | undefined): boolean => !cart?.items?.length;

const clearLegacyCartDetailsFromLocalStorage = (): void => {
  if (hasLegacyCartDetailsInLocalStorage()) {
    removeLegacyCartDetailsFromLocalStorage();
  }
};

const readPerClientCartsFromIndexedDb = async (): Promise<CartDetailsMap> => {
  const store = getBackend();
  const allKeys = await store.keys();
  const map: CartDetailsMap = {};

  for (const key of allKeys) {
    if (!isClientCartKey(key)) continue;
    const clientId = parseClientIdFromKey(key);
    if (clientId == null) continue;
    const raw = await store.getItem(key);
    const cart = parseClientCart(raw);
    if (cart && !isEmptyClientCart(cart)) {
      map[clientId] = cart;
    }
  }

  return map;
};

const removeLegacyBlobFromIndexedDb = async (): Promise<void> => {
  await getBackend().removeItem(LEGACY_BLOB_KEY);
};

const migrateLegacyBlobFromIndexedDb = async (): Promise<CartDetailsMap | null> => {
  const raw = await getBackend().getItem(LEGACY_BLOB_KEY);
  const parsed = parseLegacyCartBlob(raw);
  if (!hasCartEntries(parsed)) {
    if (raw != null) await removeLegacyBlobFromIndexedDb();
    return null;
  }

  await writeCartDetailsToIndexedDb(parsed!);
  return parsed;
};

const migrateLegacyCartDetailsFromLocalStorage = async (): Promise<CartDetailsMap | null> => {
  const fromLocal = getLegacyCartDetailsFromLocalStorage();
  if (!hasCartEntries(fromLocal)) {
    clearLegacyCartDetailsFromLocalStorage();
    return null;
  }

  try {
    await writeCartDetailsToIndexedDb(fromLocal!);
  } catch (error) {
    console.error('[cartStore] Falha ao migrar carrinho do localStorage para IndexedDB:', error);
    return fromLocal!;
  }

  clearLegacyCartDetailsFromLocalStorage();
  return fromLocal!;
};

export const readCartDetailsFromIndexedDb = async (): Promise<CartDetailsMap | null> => {
  const perClient = await readPerClientCartsFromIndexedDb();
  if (hasCartEntries(perClient)) return perClient;

  const legacyBlob = await migrateLegacyBlobFromIndexedDb();
  if (legacyBlob) return legacyBlob;

  return null;
};

export const writeCartDetailsToIndexedDb = async (details: CartDetailsMap): Promise<void> => {
  const store = getBackend();
  const allKeys = await store.keys();
  const clientIdsInPayload = new Set<number>();

  for (const [idStr, cart] of Object.entries(details)) {
    const clientId = Number(idStr);
    if (!Number.isFinite(clientId)) continue;
    clientIdsInPayload.add(clientId);

    if (isEmptyClientCart(cart)) {
      await store.removeItem(cartKey(clientId));
    } else {
      await store.setItem(cartKey(clientId), JSON.stringify(cart));
    }
  }

  for (const key of allKeys) {
    if (!isClientCartKey(key)) continue;
    const clientId = parseClientIdFromKey(key);
    if (clientId != null && !clientIdsInPayload.has(clientId)) {
      await store.removeItem(key);
    }
  }

  if (allKeys.includes(LEGACY_BLOB_KEY)) {
    await removeLegacyBlobFromIndexedDb();
  }
};

export const persistCartDetails = (details: CartDetailsMap): void => {
  void writeCartDetailsToIndexedDb(details).catch((error) => {
    console.error('[cartStore] Falha ao persistir carrinho no IndexedDB:', error);
  });
};

export const hydrateCartDetails = async (): Promise<CartDetailsMap> => {
  try {
    const perClient = await readPerClientCartsFromIndexedDb();
    if (hasCartEntries(perClient)) {
      clearLegacyCartDetailsFromLocalStorage();
      const legacyRaw = await getBackend().getItem(LEGACY_BLOB_KEY);
      if (legacyRaw != null) {
        await migrateLegacyBlobFromIndexedDb();
        const merged = await readPerClientCartsFromIndexedDb();
        return hasCartEntries(merged) ? merged : perClient;
      }
      return perClient;
    }

    const fromLegacyBlob = await migrateLegacyBlobFromIndexedDb();
    if (fromLegacyBlob) {
      clearLegacyCartDetailsFromLocalStorage();
      return fromLegacyBlob;
    }
  } catch (error) {
    console.error('[cartStore] Falha ao ler carrinho do IndexedDB:', error);
  }

  const migrated = await migrateLegacyCartDetailsFromLocalStorage();
  if (migrated) return migrated;

  return {};
};
